import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { api } from "@/lib/api";
import { eligibleSiteSet, type LaunchDateMap, type LaunchDatesResponse } from "@/lib/launchCohort";

export interface UseLaunchDatesResult {
  options: LaunchDatesResponse["options"];
  /** Earliest go-live in the CSV -- the calendar's lower bound. */
  firstLaunchDate: string | null;
  // The raw site -> go-live map. Already in the browser for the cohort filter,
  // so reading a single site's date out of it costs nothing extra. Sites with
  // no go-live scheduled are absent: the endpoint drops the 9999-12-31
  // sentinel, so a missing key means "not launched", not "unknown site".
  launchDates: LaunchDateMap;
  // The sites a cutoff keeps, or null when no cutoff is chosen. Only the raw
  // path uses it; the rollup path is filtered in SQL before it reaches here.
  eligibleSites: ReadonlySet<string> | null;
  // True while the map needed to apply a chosen cutoff is still loading. The raw
  // path must hold off rather than render unfiltered rows under a filter chip.
  pendingCohort: boolean;
}

// A rollout schedule keyed by site. It does not change while a tab is open, so
// this is fetched once and never refetched.
export function useLaunchDates(launchedBefore: string | null): UseLaunchDatesResult {
  const { data } = useQuery({
    queryKey: ["launch-dates"],
    queryFn: () => api<LaunchDatesResponse>("/api/launch-dates"),
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const eligibleSites = useMemo(
    () => (data ? eligibleSiteSet(data.launchDates, launchedBefore) : null),
    [data, launchedBefore]
  );

  const firstLaunchDate = useMemo(() => {
    if (!data) return null;
    let first: string | null = null;
    for (const date of Object.values(data.launchDates)) {
      if (first === null || date < first) first = date;
    }
    return first;
  }, [data]);

  return {
    options: data?.options ?? [],
    firstLaunchDate,
    launchDates: data?.launchDates ?? {},
    eligibleSites,
    pendingCohort: launchedBefore !== null && !data,
  };
}
